import { defaultSiteContent, type SiteContent } from '../data/site';
import { CMS_CONTENT_ROW_ID, CMS_STORAGE_BUCKET, supabase } from './supabase';

type CmsRow = {
  id: string;
  content: SiteContent;
  updated_at?: string;
};

function normalizeContent(content?: Partial<SiteContent> | null): SiteContent {
  return {
    ...defaultSiteContent,
    ...content,
    site: {
      ...defaultSiteContent.site,
      ...(content?.site || {}),
    },
    navItems: content?.navItems || defaultSiteContent.navItems,
    serviceGroups: content?.serviceGroups || defaultSiteContent.serviceGroups,
    galleryImages: content?.galleryImages || defaultSiteContent.galleryImages,
    reviews: content?.reviews || defaultSiteContent.reviews,
  };
}

export async function fetchRemoteContent() {
  if (!supabase) {
    return null;
  }

  const { data, error } = await supabase
    .from('site_content')
    .select('id, content, updated_at')
    .eq('id', CMS_CONTENT_ROW_ID)
    .maybeSingle<CmsRow>();

  if (error) {
    throw error;
  }

  if (!data?.content) {
    return null;
  }

  return normalizeContent(data.content);
}

export async function saveRemoteContent(content: SiteContent) {
  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  const payload = normalizeContent(content);

  const { error } = await supabase.from('site_content').upsert(
    {
      id: CMS_CONTENT_ROW_ID,
      content: payload,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'id' },
  );

  if (error) {
    throw error;
  }

  return payload;
}

export async function uploadCmsImage(file: File) {
  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  const extension = file.name.includes('.') ? file.name.split('.').pop() : 'jpg';
  const sanitizedBaseName = file.name.replace(/\.[^/.]+$/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const filePath = `cms/${Date.now()}-${sanitizedBaseName || 'image'}.${extension}`;

  const { error } = await supabase.storage.from(CMS_STORAGE_BUCKET).upload(filePath, file, {
    cacheControl: '3600',
    upsert: false,
  });

  if (error) {
    throw error;
  }

  const { data } = supabase.storage.from(CMS_STORAGE_BUCKET).getPublicUrl(filePath);

  return {
    path: filePath,
    publicUrl: data.publicUrl,
  };
}
